import { useMemo } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { z } from "zod";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { FieldDescription } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import { BaseForm, FormInput, FormToggle } from "components/BaseForm";
import useAuthorized from "hooks/useAuthorized";
import MetaAdAccountService from "services/MetaAdAccount";

const ACCOUNTS_QUERY_KEY = ["meta-ad-accounts"];

const makeSchema = isEdit =>
  z.object({
    name: z.string().min(1, "Name is required"),
    ad_account_id: z.string().min(1, "Ad account id is required"),
    business_id: z.string().optional(),
    page_id: z.string().min(1, "Page id is required"),
    access_token: isEdit
      ? z.string().optional()
      : z.string().min(1, "Access token is required"),
    is_sync_enabled: z.boolean(),
    sandbox_ad_account_id: z.string().optional(),
    sandbox_access_token: z.string().optional(),
  });

const AccountForm = ({ mode }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";

  const canManage = useAuthorized([
    ACL_RELATIONSHIPS.metaAdAccount.manageAccounts,
  ]);

  const {
    data: account,
    isSuccess: accountLoaded,
    isError: accountError,
  } = useQuery({
    queryKey: ["meta-ad-account", id],
    queryFn: () => MetaAdAccountService.get(id),
    enabled: isEdit && !!id,
    retry: false,
  });

  const schema = useMemo(() => makeSchema(isEdit), [isEdit]);

  const defaultValues = useMemo(
    () => ({
      name: account?.name ?? "",
      ad_account_id: account?.ad_account_id ?? "",
      business_id: account?.business_id ?? "",
      page_id: account?.page_id ?? "",
      access_token: "",
      is_sync_enabled: account?.is_sync_enabled ?? true,
      sandbox_ad_account_id: account?.sandbox_ad_account_id ?? "",
      sandbox_access_token: "",
    }),
    [account]
  );

  const handleSubmit = async values => {
    const payload = { ...values };
    // A blank token on edit retains the stored credential.
    if (isEdit && !payload.access_token) {
      delete payload.access_token;
    }
    if (isEdit && !payload.sandbox_access_token) {
      delete payload.sandbox_access_token;
    }
    if (isEdit) {
      await MetaAdAccountService.update(id, payload);
    } else {
      await MetaAdAccountService.create(payload);
    }
    queryClient.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY });
    toast.success(isEdit ? "Account updated" : "Account connected");
    navigate("/settings/integrations/meta");
  };

  const handleCancel = () => navigate("/settings/integrations/meta");

  if (!canManage) {
    return <Navigate to="/settings/integrations/meta" replace />;
  }

  if (accountError) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-[var(--color-text-muted)]">
        <p>{"Couldn't load this account. Please try again."}</p>
        <Link to="/settings/integrations/meta">Back to Meta accounts</Link>
      </div>
    );
  }

  if (isEdit && !accountLoaded) {
    return (
      <div className="tw:w-full tw:p-8 tw:text-center tw:text-[var(--color-text-muted)]">
        <Spinner />
      </div>
    );
  }

  const pageTitle = isEdit ? "Edit Meta Account" : "Connect Meta Account";

  return (
    <div className="tw:w-full tw:self-start tw:flex tw:justify-start tw:font-sans tw:text-[var(--color-text-primary)]">
      <div className="tw:flex tw:w-full tw:max-w-7xl tw:flex-col tw:gap-4">
        <BaseForm
          enablePageTitle
          enablePageTitleActions
          pageTitle={pageTitle}
          pageTitleActions={
            <FormToggle name="is_sync_enabled" label="Enable syncing" />
          }
          backLink={{
            to: "/settings/integrations/meta",
            label: "Back to Meta accounts",
          }}
          schema={schema}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          submitButtonText={isEdit ? "Save" : "Connect"}
        >
          <Card>
            <CardHeader>
              <CardTitle>Account</CardTitle>
              <CardDescription>
                {"These identifiers come from Meta Business Settings ("}
                <a
                  href="https://business.facebook.com/settings"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw:text-[#057B9F] tw:underline"
                >
                  business.facebook.com/settings
                </a>
                {
                  "). The system user must have access to both the Page and the ad account."
                }
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <FormInput name="name" label="Name" required />
                  <FieldDescription>
                    A label for this connection inside RVX-LC. It is not sent to
                    Meta.
                  </FieldDescription>
                </div>
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <FormInput
                    name="ad_account_id"
                    label="Ad Account ID"
                    required
                  />
                  <FieldDescription>
                    Business Settings → Accounts → Ad accounts, or the{" "}
                    <code>act=</code> value in the Ads Manager URL. The{" "}
                    <code>act_</code> prefix is added automatically if you omit
                    it.
                  </FieldDescription>
                </div>
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <FormInput name="business_id" label="Business ID" />
                  <FieldDescription>
                    Business Settings → Business info, or the{" "}
                    <code>business_id</code> in the settings URL. Optional.
                  </FieldDescription>
                </div>
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <FormInput name="page_id" label="Page ID" required />
                  <FieldDescription>
                    The Facebook Page that hosts your lead forms: Business
                    Settings → Accounts → Pages, or the Page&rsquo;s About →
                    Page transparency.
                  </FieldDescription>
                </div>
                <div className="tw:flex tw:flex-col tw:gap-1.5 tw:sm:col-span-2">
                  <FormInput
                    name="access_token"
                    label="System User Access Token"
                    type="password"
                    required={!isEdit}
                    placeholder={
                      isEdit
                        ? "Leave blank to keep the current token"
                        : undefined
                    }
                  />
                  <FieldDescription>
                    Business Settings → Users → System users → your system user.
                    Assign it the Page and ad account, then Generate new token
                    and select the pages and ads permissions the dialog offers.
                    Reading Lead Ads leads also requires the{" "}
                    <code>leads_retrieval</code> permission, which Meta only
                    enables after the app passes App Review and business
                    verification.
                  </FieldDescription>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Sandbox (optional)</CardTitle>
              <CardDescription>
                A Marketing API Sandbox Ad Account lets you receive read-only
                test structure without a live account. Create one in App
                Dashboard → Marketing API → Tools → Sandbox Ad Account
                Management, then use Generate Access Token. A sandbox serves
                campaigns, ad sets, and ads only — insights, pixels, and leads
                have no sandbox source.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="tw:grid tw:grid-cols-1 tw:gap-5 tw:sm:grid-cols-2">
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <FormInput
                    name="sandbox_ad_account_id"
                    label="Sandbox Ad Account ID"
                  />
                  <FieldDescription>
                    The sandbox account&rsquo;s <code>act_</code> id. The{" "}
                    <code>act_</code> prefix is added automatically if you omit
                    it.
                  </FieldDescription>
                </div>
                <div className="tw:flex tw:flex-col tw:gap-1.5">
                  <FormInput
                    name="sandbox_access_token"
                    label="Sandbox Access Token"
                    type="password"
                    placeholder={
                      isEdit
                        ? "Leave blank to keep the current token"
                        : undefined
                    }
                  />
                  <FieldDescription>
                    The token minted by Generate Access Token in Sandbox Ad
                    Account Management.
                  </FieldDescription>
                </div>
              </div>
            </CardContent>
          </Card>
        </BaseForm>
      </div>
    </div>
  );
};

export default AccountForm;
